import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import PageHero from '../components/PageHero';
import BrandProductCard, { brandProductName } from '../components/BrandProductCard';
import FaqSection from '../components/FaqSection';
import CategoryGrid from '../components/CategoryGrid';
import ContactCard from '../components/ContactCard';
import CtaPanel from '../components/CtaPanel';
import NotFound from './NotFound';
import { products } from '../data/products';
import { getBrandById } from '../data/brands';
import { getBrandProduct } from '../data/brandProducts';
import { getProductPageBySlug, productPagePath } from '../data/productPages';
import { SITE_URL } from '../data/seo';
import { company } from '../data/company';
import { quotePath } from '../utils/contactLinks';
import './LandingPage.css';
import './Products.css';

function ProductCategory() {
  const { slug } = useParams();
  const page = getProductPageBySlug(slug);
  if (!page) return <NotFound />;

  const path = productPagePath(page);
  const categoryProducts = products.filter((p) => p.categoryId === page.categoryId);
  // One entry per brand per product (products without brands stay as a single entry)
  const items = categoryProducts.flatMap((product) => {
    const productBrands = (product.brandIds || []).map(getBrandById).filter(Boolean);
    return productBrands.length
      ? productBrands.map((brand) => ({ product, brand }))
      : [{ product, brand: null }];
  });
  const breadcrumbs = [
    { name: 'Products', path: '/products' },
    { name: page.name, path },
  ];

  const itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: page.heading,
    url: `${SITE_URL}${path}`,
    itemListElement: items.map(({ product, brand }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: brandProductName(product, brand),
      description: (brand && getBrandProduct(product.id, brand.id)?.description) || product.description,
    })),
  };

  return (
    <div className="landing-page">
      <SEO
        path={path}
        title={page.title}
        description={page.description}
        keywords={page.keywords.join(', ')}
        breadcrumbs={breadcrumbs}
        structuredData={itemList}
      />

      <PageHero
        breadcrumbs={breadcrumbs}
        kicker={page.name}
        bg={page.heroBg}
        title={page.heading}
        text={page.description}
        actions={
          <>
            <Link to={quotePath} className="btn btn-sky btn-large">
              Request a quote <Icon name="arrow" />
            </Link>
            <a href={company.phones[0].href} className="btn btn-ghost btn-large">
              <Icon name="phone" /> Call {company.phones[0].number}
            </a>
          </>
        }
      />

      <section className="section section-white">
        <div className="container split split-top">
          <div className="prose">
            {page.intro.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
          <ContactCard title="Need a price or stock check?" />
        </div>
      </section>

      {items.length > 0 && (
        <section className="section section-light">
          <div className="container">
            <div className="section-head">
              <span className="pill">In stock in George</span>
              <h2 className="section-title">{page.name} in Stock in George</h2>
            </div>
            <div className="brand-product-grid">
              {items.map(({ product, brand }) => (
                <BrandProductCard key={`${product.id}-${brand ? brand.id : 'all'}`} product={product} brand={brand} />
              ))}
            </div>
          </div>
        </section>
      )}

      <FaqSection faq={page.faq} className="section-white" />

      <section className="section section-light">
        <div className="container">
          <div className="section-head-row">
            <h2 className="section-title">More Ceiling &amp; Drywall Products</h2>
            <Link to="/delivery-areas" className="text-link">
              Delivery areas <Icon name="arrow" size={16} />
            </Link>
          </div>
          <CategoryGrid exclude={page.slug} />
        </div>
      </section>

      <CtaPanel
        title="Collect in George or get it delivered"
        text="Collect from our showroom in Joubert Industria, George, or have your order delivered from Plettenberg Bay to Cape Town and into the Klein Karoo."
      />
    </div>
  );
}

export default ProductCategory;
